import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface SEOContentRendererProps {
  data: {
    query?: string | null;
    query_url?: string | null;
    readonly_url?: string | null;
    share_url?: string | null;
    status?: string | null;
    response_text?: string;
    content_score?: number | null;
    target_score?: number | null;
    passed?: boolean | null;
  };
}

/**
 * Filter out the RAW TERMS section from the markdown.
 * That section is for internal use by marketing_strategist_agent, not for the user.
 */
function filterInternalSections(markdown: string): string {
  return markdown.replace(/(-{3}\s*)?## RAW TERMS \(for content optimization\)[\s\S]*$/, '').trim();
}

const SEOContentRenderer: React.FC<SEOContentRendererProps> = ({ data }) => {
  if (!data.response_text && !data.query && !data.query_url) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="text-sm text-gray-500">No SEO content analysis available</div>
      </div>
    );
  }

  const queryId = data.query && data.query !== '/null' ? data.query : null;
  const isError = data.response_text?.startsWith('Error:');
  const isScored = data.status === 'scored' && data.content_score != null;
  const isReady = !isError && !isScored && data.status === 'ready' && data.response_text;
  const isProcessing = data.status === 'waiting' || data.status === 'in progress';
  const isCreated = !isError && !isScored && !data.status && data.response_text;

  // Error state
  if (isError) {
    return (
      <div className="bg-white border border-red-200 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-red-600 to-red-500 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-white text-sm">SEO Content Analysis</div>
            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
              Error
            </span>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-sm text-red-800">
              {data.response_text!.replace(/^Error:\s*/, '')}
            </div>
          </div>
          <div className="text-sm text-gray-600">
            This was likely a temporary issue. Type{' '}
            <span className="font-medium text-gray-800">"retry"</span> to try again.
          </div>
        </div>
      </div>
    );
  }

  // Scored state - content evaluation result
  if (isScored) {
    const score = data.content_score!;
    const target = data.target_score;
    const passed = data.passed;

    // Strip the header lines from response_text since we render them visually
    const optionsText = data.response_text
      ? data.response_text
          .replace(/^# Content Score:.*\n?/, '')
          .replace(/\*\*Target.*\n?/, '')
          .replace(/\*\*Result.*\n?/, '')
          .trim()
      : '';

    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Header with score badge */}
        <div
          className={`bg-gradient-to-r ${passed === false ? 'from-amber-600 to-orange-600' : 'from-green-600 to-emerald-600'} px-4 py-3`}
        >
          <div className="flex items-center justify-between">
            <div className="font-semibold text-white text-sm mr-2">Content Score</div>
            {passed != null && (
              <span
                className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                  passed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                }`}
              >
                {passed ? 'Above Target' : 'Below Target'}
              </span>
            )}
          </div>
        </div>

        <div className="p-4">
          {/* Score display */}
          <div className="flex items-center gap-6 mb-4">
            <div className="text-center">
              <div
                className={`text-4xl font-bold ${passed === false ? 'text-amber-600' : 'text-green-600'}`}
              >
                {score}
              </div>
              <div className="text-xs text-gray-500 mt-1">Your Score</div>
            </div>
            {target != null && (
              <>
                <div className="text-gray-300 text-2xl">/</div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-400">{target}</div>
                  <div className="text-xs text-gray-500 mt-1">Target</div>
                </div>
              </>
            )}
          </div>

          {/* Options text (if below target) */}
          {optionsText && (
            <div className="text-sm text-gray-800 leading-relaxed prose prose-sm max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{optionsText}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Ready state - rich analysis view
  if (isReady) {
    const cleanMarkdown = filterInternalSections(data.response_text!);

    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-white text-sm">SEO Content Analysis</div>
            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
              Ready
            </span>
          </div>
        </div>

        {/* Analysis content */}
        <div className="p-4">
          <div className="text-sm text-gray-800 leading-relaxed prose prose-sm max-w-none prose-headings:text-gray-900 prose-headings:mt-4 prose-headings:mb-2 prose-h2:text-base prose-h2:font-semibold prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-1 prose-h3:text-sm prose-h3:font-medium prose-p:text-gray-700 prose-p:my-1 prose-strong:text-gray-900 prose-ul:my-1 prose-ul:text-gray-700 prose-li:my-0 prose-a:text-indigo-600 prose-a:no-underline hover:prose-a:underline">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanMarkdown}</ReactMarkdown>
          </div>
          {/* Proceed prompt */}
          <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
            <div className="text-sm text-indigo-800">
              Review the analysis above and let me know when you're ready - Would you like to create
              your content?
            </div>
          </div>
          {queryId && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
              <div className="text-sm font-medium text-gray-700">Query ID:</div>
              <div className="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                {queryId}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Processing state
  if (isProcessing) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-white text-sm">SEO Content Analysis</div>
            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
              Processing
            </span>
          </div>
        </div>
        <div className="p-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-sm text-blue-800">
              Your SEO content analysis is being processed. This typically takes about 90 seconds.
              Please check back in a moment.
            </div>
          </div>
          {queryId && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
              <div className="text-sm font-medium text-gray-700">Query ID:</div>
              <div className="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                {queryId}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Created state - brief just submitted
  if (isCreated) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
          <div className="font-semibold text-white text-sm">SEO Content Analysis</div>
        </div>
        <div className="p-4">
          <div className="text-sm text-gray-700">{data.response_text}</div>
          {queryId && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
              <div className="text-sm font-medium text-gray-700">Query ID:</div>
              <div className="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                {queryId}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fallback - unknown state with some data
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
        <div className="font-semibold text-white text-sm">SEO Content Analysis</div>
      </div>
      <div className="p-4">
        {data.response_text && (
          <div className="text-sm text-gray-700 prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.response_text}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default SEOContentRenderer;
